import type { OutgoingMessage, OutgoingQueueStorage } from './types';

const databaseName: string = 'family-space-chat';
const legacyDatabaseName: string = 'family-chat';
const databaseVersion: number = 1;
const storeName: string = 'outgoing-messages';

const createIndexedDBOutgoingQueueStorage = (): OutgoingQueueStorage => {
  let database: Promise<IDBDatabase> | null = null;

  const openDatabase = (name: string): Promise<IDBDatabase> =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const request: IDBOpenDBRequest = indexedDB.open(name, databaseVersion);
      request.onupgradeneeded = (): void => {
        if (!request.result.objectStoreNames.contains(storeName)) {
          request.result.createObjectStore(storeName, { keyPath: 'clientMessageId' });
        }
      };
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => reject(request.error);
      request.onblocked = (): void => reject(new Error(`IndexedDB ${name} open was blocked`));
    });

  const openLegacyDatabase = (): Promise<IDBDatabase | null> =>
    new Promise<IDBDatabase | null>((resolve, reject) => {
      let wasMissing: boolean = false;
      const request: IDBOpenDBRequest = indexedDB.open(legacyDatabaseName);
      request.onupgradeneeded = (event: IDBVersionChangeEvent): void => {
        if (event.oldVersion === 0) {
          wasMissing = true;
          request.transaction?.abort();
        }
      };
      request.onsuccess = (): void => resolve(request.result);
      request.onerror = (): void => {
        if (wasMissing && request.error?.name === 'AbortError') {
          resolve(null);
          return;
        }
        reject(request.error);
      };
      request.onblocked = (): void => reject(new Error('Legacy IndexedDB open was blocked'));
    });

  const readMessages = (source: IDBDatabase): Promise<OutgoingMessage[]> =>
    new Promise<OutgoingMessage[]>((resolve, reject) => {
      if (!source.objectStoreNames.contains(storeName)) {
        resolve([]);
        return;
      }
      const transaction: IDBTransaction = source.transaction(storeName, 'readonly');
      const request: IDBRequest<OutgoingMessage[]> = transaction.objectStore(storeName).getAll();
      transaction.oncomplete = (): void => resolve(request.result);
      transaction.onerror = (): void => reject(transaction.error);
      transaction.onabort = (): void => reject(transaction.error);
    });

  const copyMissingMessages = async (
    source: IDBDatabase,
    destination: IDBDatabase,
  ): Promise<void> => {
    const messages: OutgoingMessage[] = await readMessages(source);
    if (messages.length === 0) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const transaction: IDBTransaction = destination.transaction(storeName, 'readwrite');
      const store: IDBObjectStore = transaction.objectStore(storeName);
      const keysRequest: IDBRequest<IDBValidKey[]> = store.getAllKeys();
      keysRequest.onsuccess = (): void => {
        const existingIds: Set<IDBValidKey> = new Set<IDBValidKey>(keysRequest.result);
        for (const message of messages) {
          if (!existingIds.has(message.clientMessageId)) {
            store.add(message);
          }
        }
      };
      transaction.oncomplete = (): void => resolve();
      transaction.onerror = (): void => reject(transaction.error);
      transaction.onabort = (): void => reject(transaction.error);
    });
  };

  const initializeDatabase = async (): Promise<IDBDatabase> => {
    let legacyDatabase: IDBDatabase | null;
    try {
      legacyDatabase = await openLegacyDatabase();
    } catch {
      return openDatabase(legacyDatabaseName);
    }
    if (legacyDatabase === null) {
      return openDatabase(databaseName);
    }

    let currentDatabase: IDBDatabase | null = null;
    try {
      currentDatabase = await openDatabase(databaseName);
      await copyMissingMessages(legacyDatabase, currentDatabase);
      legacyDatabase.close();
      return currentDatabase;
    } catch {
      currentDatabase?.close();
      return legacyDatabase;
    }
  };

  const getDatabase = (): Promise<IDBDatabase> => {
    if (database) {
      return database;
    }
    database = initializeDatabase();
    return database;
  };

  const transaction = async (
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest | void,
  ): Promise<IDBRequest | void> => {
    const db = await getDatabase();
    return new Promise<IDBRequest | void>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const request = action(tx.objectStore(storeName));
      tx.oncomplete = (): void => resolve(request);
      tx.onerror = (): void => reject(tx.error);
      tx.onabort = (): void => reject(tx.error);
    });
  };

  return {
    load: async (): Promise<OutgoingMessage[]> => {
      const request = (await transaction('readonly', (store) => store.getAll())) as IDBRequest<
        OutgoingMessage[]
      >;
      return request.result;
    },
    remove: async (clientMessageId: string): Promise<void> => {
      await transaction('readwrite', (store) => store.delete(clientMessageId));
    },
    save: async (message: OutgoingMessage): Promise<void> => {
      await transaction('readwrite', (store) => store.put(message));
    },
  };
};

export { createIndexedDBOutgoingQueueStorage };
